import { useState, useRef, useCallback } from 'react';
import { Upload, Download, FileSpreadsheet, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { universalBulkImport } from 'wasp/client/operations';
import toast from 'react-hot-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import { parseXlsx, type ParseResult } from '../utils/xlsxParser';
import { generateUniversalTemplate } from '../utils/xlsxTemplateGenerator';
import { resolveExecutionOrder } from '../utils/dependencyResolver';
import { validateSheets, type ValidationWarning } from '../utils/preflightValidator';

type Step = 'upload' | 'preview' | 'results';

interface UniversalImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete?: () => void;
}

type ImportResult = {
  created: number;
  updated: number;
  skipped: number;
  errors: number;
  total: number;
  details: any;
};

export function UniversalImportDialog({ open, onOpenChange, onComplete }: UniversalImportDialogProps) {
  const [step, setStep] = useState<Step>('upload');
  const [fileName, setFileName] = useState<string>('');
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [overwrite, setOverwrite] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = useCallback(() => {
    setStep('upload');
    setFileName('');
    setParseResult(null);
    setOverwrite(false);
    setIsSubmitting(false);
    setResult(null);
    setParseError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) reset();
    onOpenChange(newOpen);
  };

  const handleDownloadTemplate = () => {
    try {
      generateUniversalTemplate();
      toast.success('Template downloaded successfully');
    } catch (err: any) {
      toast.error('Failed to generate template: ' + err.message);
    }
  };

  const handleFileSelect = async (file: File) => {
    setParseError(null);
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setParseError('Please select an XLSX file.');
      return;
    }

    try {
      const buffer = await file.arrayBuffer();
      const parsed = parseXlsx(buffer);

      if (parsed.errors.length > 0) {
        setParseError(parsed.errors.join('; '));
        return;
      }

      if (parsed.totalRows === 0) {
        setParseError('No data found in workbook.');
        return;
      }

      if (parsed.totalRows > 2000) {
        setParseError(`Total rows (${parsed.totalRows}) exceeds the 2,000 row limit for universal imports.`);
        return;
      }

      setFileName(file.name);
      setParseResult(parsed);
      setStep('preview');
    } catch (err: any) {
      setParseError(err.message || 'Failed to parse XLSX file.');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleSubmit = async () => {
    if (!parseResult || !fileName) return;
    setIsSubmitting(true);

    try {
      const importResult = await universalBulkImport({
        fileName,
        overwrite,
        sheets: parseResult.sheets,
      });

      setResult(importResult as ImportResult);
      setStep('results');
      toast.success('Import completed');
      if (onComplete) onComplete();
    } catch (err: any) {
      toast.error(err.message || 'Import failed');
      setParseError(err.message || 'Import failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render steps
  // ---------------------------------------------------------------------------

  const renderUploadStep = () => (
    <div className="space-y-4">
      <div
        className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary transition-colors cursor-pointer"
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileInputRef.current?.click()}
      >
        <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
        <p className="text-sm font-medium mb-1">Drag & drop your XLSX file here</p>
        <p className="text-xs text-muted-foreground mb-3">or click to browse</p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileSelect(file);
          }}
          className="hidden"
        />
      </div>

      {parseError && (
        <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
          <XCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
          <p className="text-sm text-destructive">{parseError}</p>
        </div>
      )}

      <Button variant="outline" onClick={handleDownloadTemplate} className="w-full">
        <Download className="h-4 w-4 mr-2" />
        Download Template
      </Button>

      <div className="p-3 bg-muted rounded-md">
        <p className="text-xs font-medium mb-1">Required columns by sheet:</p>
        <p className="text-xs text-muted-foreground">
          location_types: key, label • organization_types: key, label • topic_types: key, label • service_types: key, label • locations: name • organizations: name, slug, organizationTypeKey • topics: name, slug, topicTypeKey • sections: section_key, label_en • service_definitions: name, slug, serviceTypeKey • users: email
        </p>
      </div>

      <div className="flex items-center justify-between p-3 bg-muted rounded-md">
        <div>
          <p className="text-sm font-medium">Overwrite existing records</p>
          <p className="text-xs text-muted-foreground">Update records that already exist</p>
        </div>
        <Switch checked={overwrite} onCheckedChange={setOverwrite} />
      </div>

      {overwrite && (
        <div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
          <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-yellow-800 dark:text-yellow-200">
            Overwrite mode is enabled. Existing records will be updated with the values in this file. Use with caution.
          </p>
        </div>
      )}
    </div>
  );

  const renderPreviewStep = () => {
    if (!parseResult) return null;
    const executionOrder = resolveExecutionOrder(parseResult.sheets);
    const warnings = validateSheets(parseResult.sheets);
    const hasErrors = warnings.some((w) => w.severity === 'error');

    return (
      <div className="space-y-4">
        <div className="p-4 bg-muted rounded-lg">
          <h4 className="font-medium mb-2">Validation Results</h4>
          <div className="space-y-1 text-sm">
            {Object.entries(parseResult.sheets).map(([sheetName, rows]) => (
              <div key={sheetName} className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="capitalize">{sheetName.replace(/_/g, ' ')}</span>
                <span className="text-muted-foreground">— {rows.length} rows</span>
              </div>
            ))}
          </div>

          {/* Warnings/Errors */}
          {warnings.length > 0 && (
            <div className="mt-3 space-y-2">
              {warnings.map((warning, i) => (
                <div
                  key={i}
                  className={`flex items-start gap-2 p-2 rounded ${
                    warning.severity === 'error'
                      ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                      : 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800'
                  }`}
                >
                  {warning.severity === 'error' ? (
                    <XCircle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-500 mt-0.5 flex-shrink-0" />
                  )}
                  <div className="text-xs">
                    <span className="font-medium capitalize">{warning.sheet.replace(/_/g, ' ')}:</span>{' '}
                    <span
                      className={
                        warning.severity === 'error'
                          ? 'text-red-800 dark:text-red-200'
                          : 'text-yellow-800 dark:text-yellow-200'
                      }
                    >
                      {warning.message}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {parseResult.unknownSheets.length > 0 && (
            <div className="mt-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
              <p className="text-xs text-yellow-800 dark:text-yellow-200">
                Unknown sheets ignored: {parseResult.unknownSheets.join(', ')}
              </p>
            </div>
          )}

          <div className="mt-3 pt-3 border-t">
            <p className="text-xs text-muted-foreground">
              <strong>Execution order:</strong> {executionOrder.map(s => s.replace(/_/g, ' ')).join(' → ')}
            </p>
          </div>

          <div className="mt-3 pt-3 border-t">
            <p className="text-xs">
              <strong>Total rows:</strong> {parseResult.totalRows}
            </p>
            <p className="text-xs">
              <strong>Overwrite mode:</strong> {overwrite ? 'Enabled' : 'Disabled'}
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setStep('upload')} className="flex-1">
            Back
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || hasErrors} className="flex-1">
            {hasErrors ? 'Fix Errors First' : isSubmitting ? 'Importing...' : 'Import'}
          </Button>
        </div>
        {hasErrors && (
          <p className="text-xs text-destructive text-center">
            Please fix validation errors before importing
          </p>
        )}
      </div>
    );
  };

  const renderResultsStep = () => {
    if (!result) return null;

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
            <p className="text-xs text-muted-foreground mb-1">Created</p>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">{result.created}</p>
          </div>
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
            <p className="text-xs text-muted-foreground mb-1">Updated</p>
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{result.updated}</p>
          </div>
          <div className="p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md">
            <p className="text-xs text-muted-foreground mb-1">Skipped</p>
            <p className="text-2xl font-bold text-muted-foreground">{result.skipped}</p>
          </div>
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <p className="text-xs text-muted-foreground mb-1">Errors</p>
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">{result.errors}</p>
          </div>
        </div>

        <div className="p-3 bg-muted rounded-md">
          <p className="text-xs text-muted-foreground">
            Import logged to history. View the "Import History" tab for per-row details.
          </p>
        </div>

        <Button onClick={() => handleOpenChange(false)} className="w-full">
          Close
        </Button>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Universal Import
          </DialogTitle>
          <DialogDescription>
            Import data across all entity types in a single XLSX file
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {step === 'upload' && renderUploadStep()}
          {step === 'preview' && renderPreviewStep()}
          {step === 'results' && renderResultsStep()}
        </div>
      </DialogContent>
    </Dialog>
  );
}



